import { For, Match, Show, Switch, createContext, createEffect, createMemo, createSignal } from 'solid-js'
import { createSharedStore, debounce, requireContext } from 'common'
import { ParentSelectContext } from "./ParentSelectContext"
import { Image, Flexbox, requireDataStoreContext, requireFocusContext, requireViewContext, Dropdown, Button, Combobox, Icon, useViewContext, DataStore, DataStoreContext, Loader, GridLayout } from 'st-app'
import { LookupValuePair, ViewState } from 'st-app/api'
import { parentSelect as api } from 'sis-app/api'

import styles from './sis-parent-select.module.css'

const ParentSelectContextJsx = createContext<ParentSelectContext>();
export const requireParentSelectContext = () => {
    return requireContext(ParentSelectContextJsx);
}

export class ParentSelectProps {
    identity?: string | undefined = '';
    suppressDropdown?: boolean = false;
}

export default function ParentSelect(props: ParentSelectProps) {
    const context = new ParentSelectContext();

    return (
        <ParentSelectContextJsx.Provider value={context}>
            <ParentSelectInternal {...props} />
        </ParentSelectContextJsx.Provider>
    )
}

function ParentSelectInternal(props: ParentSelectProps) {
    const viewContext = useViewContext();
    const dataStore = requireDataStoreContext();
    const [isLoading, setIsLoading] = createSignal(false);

    const [search, setSearch] = createSignal('');
    const [value, setValue] = createSignal<LookupValuePair>();
    const [searchResults, setSearchResults] = createSignal<LookupValuePair[]>([]);
    const [showDropdown, setShowDropdown] = createSignal(false);

    const formattedName = createMemo(() => dataStore.get('currentValue', 'K12-ParentGuardianInfo-Parent-FormattedName', value()?.value));

    const find = debounce(async (s: string) => {
        if (!s) {
            setSearchResults([]);
        }
        else {
            setIsLoading(true);
            try {
                const viewState = viewContext?.viewState();
                const findResults = await api.Find(viewState ?? new ViewState(), s);

                for (const par of findResults) {
                    dataStore.setCurrentValue('K12-ParentGuardianInfo-Parent-Photo', par.photo, par.parentGU);
                    dataStore.setCurrentValue('K12-ParentGuardianInfo-Parent-FormattedName', par.formattedName, par.parentGU);
                }

                setSearchResults(findResults.map((par) => ({
                    value: par.parentGU,
                    displayValue: par.formattedName
                })));
            }
            finally {
                setIsLoading(false);
            }
        }
    }, 500);

    const [contRef, setContRef] = createSignal<HTMLDivElement>();
    createEffect(() => find(search()));
    createEffect(() => {
        const val = value();

        if (val) {
            setShowDropdown(false);
            dataStore.setCurrentValue('K12-ParentGuardianInfo-Parent-ParentGU', val.value);
        }
    });

    function clearValue() {
        setValue(undefined);
        setShowDropdown(false);
    }

    function launch() {
        if (!value()) {
            setShowDropdown(true);
        }
    }

    return <>
        <Flexbox dir="vertical">
            <Flexbox middle dir="horizontal" class={styles.input} ref={setContRef} onClick={launch} style="padding: 2px 6px" >
                <Flexbox.Item as="button" aria-label="Select a Parent" auto class={`${styles.value} ${styles.btn}`} >
                    {formattedName()}
                </Flexbox.Item>
                <Show when={!props.suppressDropdown}>
                    <Flexbox.Item class={styles.addon} >
                        <Switch fallback={<Loader size="small" />}>
                            <Match when={!isLoading()}>
                                <Button type='link' label="Edit" style="padding: 0px" onClick={() => setShowDropdown(true)} >
                                    <Icon src="fas fa-link" />
                                </Button>
                            </Match>
                        </Switch>
                    </Flexbox.Item>
                </Show>
            </Flexbox>
            <ParentFKValue />
        </Flexbox>

        <Dropdown open={[showDropdown, setShowDropdown]} placement='bottom-start' target={contRef()} >
            <Dropdown.Content>
                <Combobox value={[value, setValue]} filter={[search, setSearch]} filterIcon="fas fa-search" items={searchResults()} fallback={<Flexbox.Item><i>No Results</i></Flexbox.Item>} >{it =>
                    <ParentFKRow size={32} identity={it.value} />
                }</Combobox>
                <Flexbox dir="horizontal">
                    <Flexbox.Item auto></Flexbox.Item>
                    <Flexbox.Item>
                        <Button type="link" onClick={clearValue}>clear</Button>
                    </Flexbox.Item>
                </Flexbox>
            </Dropdown.Content>
        </Dropdown>
    </>
}

function ParentFKValue() {
    const dataStore = requireDataStoreContext();
    const viewCtx = useViewContext();

    const parentGU = createMemo(() => dataStore.get('currentValue', 'K12-ParentGuardianInfo-Parent-ParentGU'));
    const formattedName = createMemo(() => dataStore.get('currentValue', 'K12-ParentGuardianInfo-Parent-FormattedName', parentGU()));
    const photoSrc = createMemo(() => dataStore.get('currentValue', 'K12-ParentGuardianInfo-Parent-Photo', parentGU()));

    return <Flexbox dir="horizontal" class={styles['parent-info']} >
        <Flexbox auto dir="vertical">
            <Flexbox.Item style={{ "font-weight": 'bold', 'font-size': '1.2em' }} >
                {formattedName()}
            </Flexbox.Item>
        </Flexbox>
        <Flexbox.Item>
            <Image useDefaultPhoto src={photoSrc()} alt="Parent Photo" width="80px" style="max-height: 150px" />
        </Flexbox.Item>
    </Flexbox>
}

function ParentFKRow(props: { identity?: string, size?: number }) {
    //const focus = requireFocusContext();
    const dataStore = requireDataStoreContext();
    const viewCtx = useViewContext();

    const photoSrc = createMemo(() => dataStore.get('currentValue', 'K12-ParentGuardianInfo-Parent-Photo', props.identity));
    const formattedName = createMemo(() => dataStore.get('currentValue', 'K12-ParentGuardianInfo-Parent-FormattedName', props.identity));

    return <Flexbox dir="horizontal">
        <Flexbox auto dir="vertical">
            <Flexbox.Item style={{ "font-weight": 'bold', 'font-size': '1.2em' }} >
                {formattedName()}
            </Flexbox.Item>
        </Flexbox>
        <Flexbox.Item>
            <Image useDefaultPhoto src={photoSrc()} alt="Parent Photo" width="30" style="max-height: 150px" />
        </Flexbox.Item>
    </Flexbox>
}

ParentSelect.defaultProps = new ParentSelectProps();
